// frontend/src/widgets/WidgetRenderer.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-07-30
// Updated:      2026-07-30
//
// Description:  Shared registry-backed renderer for configurable page widgets. Resolves the
//               canonical component, applies page-supplied business props, and renders it through
//               the standard WidgetWrapper without page-specific component switch statements.
//
// Change Summary (2026-07-30):
//   - Added canonical registry lookup and transitional id normalisation.
//   - Added surface validation so a page cannot render a widget outside its allow-list.
//   - Added consistent unknown-widget and render-failure states.
//   - Preserves page ownership of business data, callbacks, modal state, and clipping behaviour.
//
// Operational Notes:
//   - Pages must pass widget-specific props through `widgetProps`; this renderer does not own
//     business logic and does not invent a universal business context object.
//   - Widget components are imported only by WidgetRegistry.js after a page is migrated.
//   - ErrorBoundary prevents one defective widget from taking down the entire configurable page.

import React from "react";
import WidgetWrapper from "../components/WidgetWrapper";
import { getWidgetById, getWidgetTitle } from "./WidgetRegistry";
import { migrateWidgetId } from "./WidgetMigration";
import { isWidgetAvailableOnSurface } from "./WidgetSurface";

class WidgetRenderErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Widget render failed", {
      widgetId: this.props.widgetId,
      error,
      errorInfo,
    });
  }

  componentDidUpdate(previousProps) {
    if (previousProps.widgetId !== this.props.widgetId && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div role="alert" style={{ padding: 12, color: "#b91c1c", fontSize: 13 }}>
          This widget could not be rendered.
        </div>
      );
    }

    return this.props.children;
  }
}

const WidgetRenderer = ({
  widgetId,
  surface,
  widgetProps = {},
  title,
  onRemove,
  showRemoveButton = true,
  wrapperProps = {},
}) => {
  const canonicalId = migrateWidgetId(widgetId);
  const entry = getWidgetById(canonicalId);

  if (!entry) {
    return (
      <WidgetWrapper
        title={title || getWidgetTitle(canonicalId)}
        onRemove={onRemove}
        showRemoveButton={showRemoveButton}
        {...wrapperProps}
      >
        <div role="alert" style={{ padding: 12, color: "#b91c1c", fontSize: 13 }}>
          Unknown widget: {String(canonicalId)}
        </div>
      </WidgetWrapper>
    );
  }

  if (surface && !isWidgetAvailableOnSurface(surface, canonicalId)) {
    return (
      <WidgetWrapper
        title={title || getWidgetTitle(canonicalId)}
        onRemove={onRemove}
        showRemoveButton={showRemoveButton}
        {...wrapperProps}
      >
        <div role="alert" style={{ padding: 12, color: "#b91c1c", fontSize: 13 }}>
          Widget {canonicalId} is not registered for {surface}.
        </div>
      </WidgetWrapper>
    );
  }

  const Component = entry.component;

  if (typeof Component !== "function" && typeof Component !== "object") {
    return (
      <WidgetWrapper
        title={title || getWidgetTitle(canonicalId)}
        onRemove={onRemove}
        showRemoveButton={showRemoveButton}
        {...wrapperProps}
      >
        <div role="alert" style={{ padding: 12, color: "#b91c1c", fontSize: 13 }}>
          Widget component is invalid: {canonicalId}
        </div>
      </WidgetWrapper>
    );
  }

  return (
    <WidgetWrapper
      title={title || entry.friendly_name || getWidgetTitle(canonicalId)}
      onRemove={onRemove}
      showRemoveButton={showRemoveButton}
      {...wrapperProps}
    >
      <WidgetRenderErrorBoundary widgetId={canonicalId}>
        <Component {...widgetProps} />
      </WidgetRenderErrorBoundary>
    </WidgetWrapper>
  );
};

export default WidgetRenderer;
